'use client';

import React, { useEffect, useRef, useState, useId } from 'react';
import { useTheme } from 'next-themes';

export interface MermaidProps {
  /** Mermaid diagram source (preferred — use this instead of children). */
  chart?: string;
  /** Mermaid diagram source passed as JSX children (legacy). */
  children?: string;
  caption?: string;
  wide?: boolean;
}

// Cache the dynamically-imported mermaid module so toggling themes doesn't
// re-download it, and track the last theme we initialized with so we only
// call `mermaid.initialize` when it actually changes.
let mermaidModulePromise: Promise<typeof import('mermaid').default> | null = null;
let initializedTheme: 'dark' | 'light' | null = null;

function loadMermaid() {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import('mermaid').then((m) => m.default);
  }
  return mermaidModulePromise;
}

const DARK_THEME_VARS = {
  primaryColor: '#3b3b4f',
  primaryTextColor: '#e5e5e5',
  primaryBorderColor: '#555',
  lineColor: '#888',
  secondaryColor: '#2a2a3a',
  tertiaryColor: '#1e1e2e',
  background: '#1e1e2e',
  mainBkg: '#2a2a3a',
  nodeBorder: '#555',
  clusterBkg: '#2a2a3a',
  titleColor: '#e5e5e5',
  edgeLabelBackground: '#2a2a3a',
  noteBkgColor: '#3b3b4f',
  noteTextColor: '#e5e5e5',
} as const;

const LIGHT_THEME_VARS = {
  primaryColor: '#f0f0f0',
  primaryTextColor: '#404040',
  primaryBorderColor: '#d5d5d5',
  lineColor: '#888',
  secondaryColor: '#fafafa',
  tertiaryColor: '#fff',
  background: '#ffffff',
  mainBkg: '#f5f5f5',
  nodeBorder: '#d5d5d5',
  clusterBkg: '#fafafa',
  titleColor: '#171717',
  edgeLabelBackground: '#ffffff',
  noteBkgColor: '#f5f5f5',
  noteTextColor: '#404040',
} as const;

export function Mermaid({ chart, children, caption, wide }: MermaidProps) {
  const code = (chart || children || '').trim();
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!code);
  const { resolvedTheme } = useTheme();
  const instanceId = useId();
  const diagramId = `mermaid-${instanceId.replace(/:/g, '')}`;

  useEffect(() => {
    if (!code) return;
    let cancelled = false;

    async function renderDiagram() {
      if (!containerRef.current) return;

      try {
        // Wait for fonts to load so Mermaid measures text correctly
        await document.fonts.ready;
        const mermaid = await loadMermaid();

        const nextTheme: 'dark' | 'light' = resolvedTheme === 'dark' ? 'dark' : 'light';
        if (initializedTheme !== nextTheme) {
          mermaid.initialize({
            startOnLoad: false,
            theme: nextTheme === 'dark' ? 'dark' : 'default',
            themeVariables: nextTheme === 'dark' ? DARK_THEME_VARS : LIGHT_THEME_VARS,
            fontSize: 14,
            securityLevel: 'strict',
            flowchart: {
              htmlLabels: true,
              padding: 16,
              useMaxWidth: true,
            },
            sequence: {
              useMaxWidth: true,
            },
          });
          initializedTheme = nextTheme;
        }

        const { svg } = await mermaid.render(diagramId, code);

        if (!cancelled && containerRef.current) {
          containerRef.current.innerHTML = svg;

          // Make the SVG responsive while preserving text readability
          const svgEl = containerRef.current.querySelector('svg');
          if (svgEl) {
            const origWidth = svgEl.getAttribute('width');
            const origHeight = svgEl.getAttribute('height');
            // Ensure viewBox is set for proper scaling
            if (!svgEl.getAttribute('viewBox') && origWidth && origHeight) {
              const w = parseFloat(origWidth);
              const h = parseFloat(origHeight);
              if (w && h) {
                svgEl.setAttribute('viewBox', `0 0 ${w} ${h}`);
              }
            }
            svgEl.removeAttribute('width');
            svgEl.removeAttribute('height');
            svgEl.style.width = '100%';
            svgEl.style.height = 'auto';
            svgEl.style.maxWidth = '100%';
          }

          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to render diagram';
          setError(message);
          setLoading(false);
        }
      }
    }

    setLoading(true);
    renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [code, resolvedTheme, diagramId]);

  return (
    <figure className={`mermaid-diagram${wide ? ' wide-breakout' : ''}`}>
      {loading && (
        <div className="mermaid-loading" aria-live="polite">
          <div className="mermaid-loading-spinner" />
          <span>Rendering diagram…</span>
        </div>
      )}
      {error && (
        <div className="mermaid-error" role="alert">
          <strong>Diagram error</strong>
          <pre>{error}</pre>
        </div>
      )}
      <div
        ref={containerRef}
        className="mermaid-svg"
        role="img"
        aria-label={caption || 'Mermaid diagram'}
        style={{ display: loading ? 'none' : 'block' }}
      />
      {caption && <figcaption className="mermaid-caption">{caption}</figcaption>}
    </figure>
  );
}
